package dev.lingvo.site.components.marketing

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.rotate
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import dev.lingvo.site.ds.HIT_AREA_FLOOR_PX
import dev.lingvo.site.ds.Elevation
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.elevation
import dev.lingvo.site.ds.transition
import dev.lingvo.site.ds.transitionColors
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.StylePropertyValue
import org.jetbrains.compose.web.css.deg
import org.jetbrains.compose.web.css.px

val DocsLayoutStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .maxWidth(Space.ContentMax)
            .gap(Space.XXXL)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "minmax(0, 1fr)")
                property("margin", "0 auto")
                property("align-items", "start")
            }
    }
    Breakpoint.LG {
        Modifier.styleModifier { property("grid-template-columns", "15rem minmax(0, 1fr)") }
    }
}

val DocsRailStyle = CssStyle {
    base {
        // Below the two-column breakpoint the rail would be a list of links stacked above the
        // document it describes, which is a table of contents nobody scrolls back up to use.
        // [DocsContentsStyle] carries the same list, collapsed, for those widths.
        Modifier.styleModifier { property("display", "none") }
    }
    Breakpoint.LG {
        Modifier
            .position(Position.Sticky)
            // Clears the 64px public header, which is itself sticky.
            .top(80.px)
            .styleModifier {
                property("display", "block")
                property("max-height", "calc(100dvh - 104px)")
                property("overflow-y", "auto")
                property("overscroll-behavior", "contain")
            }
    }
}


/**
 * The contents, collapsed, for every width [DocsRailStyle] hides itself at.
 *
 * Hiding the rail below LG left a ten-section page with no in-page navigation of any kind on the
 * widths most readers arrive at: the only route to section eight was scrolling past seven. Stacking
 * the rail's ten links above the document instead would push the document itself off the first
 * screen, so this is the same list behind the browser's own `details`, costing a phone one row until
 * it is asked for.
 */
val DocsContentsStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .backgroundColor(colorMode.toSitePalette().surfaceRaised)
            .borderRadius(Radius.Control)
            .position(Position.Sticky)
            .top(72.px)
            .styleModifier {
                property("z-index", "20")
                property("--docs-motion-duration", "${Motion.FastMs}ms")
                property("--docs-motion-offset", "-4px")
            }
    }
    cssRule("[open]") {
        Modifier.styleModifier { property("--docs-motion-duration", "${Motion.StandardMs}ms") }
    }
    cssRule("[open] [data-docs-chevron]") { Modifier.rotate(180.deg) }
    // Native disclosure remains usable without this progressive enhancement.
    cssRule("::details-content") {
        Modifier.styleModifier {
            property("opacity", "0")
            property("transform", "translateY(var(--docs-motion-offset))")
            property("transition", "opacity var(--docs-motion-duration) ${Motion.EaseOut}, " +
                "transform var(--docs-motion-duration) ${Motion.EaseOut}, " +
                "content-visibility var(--docs-motion-duration) allow-discrete")
        }
    }
    cssRule("[open]::details-content") {
        Modifier.styleModifier {
            property("opacity", "1")
            property("transform", "translateY(0)")
        }
    }
    cssRule("[data-docs-keyboard]") {
        Modifier.styleModifier {
            property("--docs-motion-duration", "0ms")
            property("--docs-motion-offset", "0px")
        }
    }
    cssRule(CSSMediaQuery.MediaFeature("prefers-reduced-motion", StylePropertyValue("reduce"))) {
        Modifier.styleModifier {
            property("--docs-motion-duration", "0ms")
            property("--docs-motion-offset", "0px")
        }
    }
    Breakpoint.LG {
        // The rail is back at this width, and two tables of contents on one page is one too many.
        Modifier.styleModifier { property("display", "none") }
    }
}

val DocsContentsSummaryStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .minHeight(48.px)
            .padding(topBottom = Space.SM, leftRight = Space.LG)
            .gap(Space.MD)
            .color(colorMode.toSitePalette().textSecondary)
            .fontSize(TypeScale.Small)
            .fontWeight(500)
            .borderRadius(Radius.Control)
            .outline(0.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("cursor", "pointer")
                // Chrome and Firefox draw a disclosure triangle from the list marker.
                property("list-style", "none")
            }
            .transitionColors()
    }
    // Safari draws its own triangle from a pseudo-element that `list-style` does not reach.
    cssRule("::-webkit-details-marker") { Modifier.styleModifier { property("display", "none") } }
    hover {
        Modifier.color(colorMode.toSitePalette().textPrimary).backgroundColor(colorMode.toSitePalette().surfaceSunken)
    }
    focusVisible {
        Modifier.backgroundColor(colorMode.toSitePalette().accentSubtle)
            .styleModifier { property("text-decoration", "underline"); property("text-underline-offset", "4px") }
    }
}

val DocsContentsListStyle = CssStyle.base {
    Modifier
        .backgroundColor(colorMode.toSitePalette().surfaceRaised)
        .borderRadius(Radius.Card)
        .elevation(Elevation.Floating)
        .position(Position.Absolute)
        .styleModifier {
            property("top", "calc(100% + 8px)")
            property("max-height", "min(65dvh, 32rem)")
            property("overflow-y", "auto")
            property("overscroll-behavior", "contain")
        }
        .fillMaxWidth()
        .padding(Space.SM)
}

val DocsRailLinkStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = Space.XS, leftRight = Space.SM)
            .color(colorMode.toSitePalette().textSecondary)
            .fontSize(TypeScale.Small)
            .borderRadius(Radius.Control)
            .outline(0.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier {
                property("display", "block")
                property("text-decoration", "none")
                property("overflow-wrap", "anywhere")
            }
            .transitionColors()
    }
    cssRule("[aria-current=\"location\"]") {
        Modifier.color(colorMode.toSitePalette().accent)
            .backgroundColor(colorMode.toSitePalette().accentSubtle)
            .fontWeight(600)
    }
    hover {
        Modifier
            .color(colorMode.toSitePalette().textPrimary)
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
    }
    focusVisible {
        Modifier.backgroundColor(colorMode.toSitePalette().accentSubtle)
            .styleModifier { property("text-decoration", "underline"); property("text-underline-offset", "4px") }
    }
}
